import React, { useState } from 'react';
import { arrayOf, shape, string, func } from 'prop-types';
import classNames from 'classnames';
import css from './LocaleSelector.module.css';

/**
 * LocaleSelector — language switcher dropdown.
 *
 * Stores the selected locale in localStorage and reloads the page
 * so the new locale is applied (Sharetribe loads locale at server render time).
 *
 * For client-side locale switching without reload, integrate with the Redux
 * store and update the IntlProvider locale prop directly.
 *
 * Usage (in Topbar):
 *   {features.multiLanguage && (
 *     <LocaleSelector
 *       locales={[
 *         { code: 'en', label: 'English', flag: '🇬🇧' },
 *         { code: 'lv', label: 'Latviešu', flag: '🇱🇻' },
 *         { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
 *       ]}
 *       currentLocale="en"
 *       onChange={locale => setLocale(locale)}
 *     />
 *   )}
 *
 * @param {Array}    props.locales  — available locales [{ code, label, flag? }]
 * @param {string}   props.currentLocale — current active locale code
 * @param {Function} [props.onChange]   — called with new locale code
 */
const LocaleSelector = ({ locales, currentLocale, onChange, className }) => {
  const [isOpen, setIsOpen] = useState(false);

  const current = locales.find(l => l.code === currentLocale) || locales[0];

  const handleSelect = (locale) => {
    setIsOpen(false);
    if (locale.code === currentLocale) return;

    // Save to localStorage
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('locale', locale.code);
    }

    onChange && onChange(locale.code);
  };

  if (locales.length <= 1) return null;

  return (
    <div className={classNames(css.root, className)}>
      <button
        type="button"
        onClick={() => setIsOpen(prev => !prev)}
        className={css.trigger}
      >
        {current.flag && <span className={css.flag}>{current.flag}</span>}
        <span className={css.code}>{current.code.toUpperCase()}</span>
        <svg className={css.chevron} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div className={css.dropdown}>
          {locales.map(locale => (
            <button
              key={locale.code}
              type="button"
              onClick={() => handleSelect(locale)}
              className={classNames(css.option, locale.code === currentLocale && css.active)}
            >
              {locale.flag && <span className={css.flag}>{locale.flag}</span>}
              <span>{locale.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

LocaleSelector.defaultProps = { onChange: null, className: null };
LocaleSelector.propTypes = {
  locales: arrayOf(shape({ code: string.isRequired, label: string.isRequired, flag: string })).isRequired,
  currentLocale: string.isRequired,
  onChange: func,
  className: string,
};

export default LocaleSelector;
